import {
  Box,
  Collapse,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslations } from "hooks/useTranslations";
import { IItinerary, formatPrice, useConfig } from "@thekilroygroup/shared-ui";
import { useSelector } from "react-redux";
import {
  getSubTotal,
  getTotal,
  shoppingCartSelector,
} from "redux/shoppingCart";
import { useCountUp } from "use-count-up";
import { getCurrency } from "utils/numberUtils";

interface IProps {
  itinerary: IItinerary;
}
export const Summary = (props: IProps) => {
  const { itinerary } = props;
  const { breakpoints } = useTheme();
  const isTablet = useMediaQuery(breakpoints.up("sm"));
  const isDesktop = useMediaQuery(breakpoints.up("md"));
  const {
    translations: {
      paymentMethod: {
        paymentPriceSummary: {
          priceSummaryLabel,
          discount,
          total,
          totalPriceToPay,
        },
      },
    },
  } = useTranslations();
  const { locale } = useConfig();

  const getStringifiedDiscount = (price: string): string => {
    return parseInt(price) > 0 ? `-${price}` : price;
  };

  const shoppingCart = useSelector(shoppingCartSelector);
  const subTotalAmount = useSelector(getSubTotal);
  const totalAmount = useSelector(getTotal);
  const discountAmount =
    shoppingCart?.selectedPaymentMethod?.discount.amount || 0;

  /**
   * Animated prices start
   */
  const [localSubTotalAmount, setLocalSubTotalAmount] =
    useState(subTotalAmount);

  const [localDiscountAmount, setLocalDiscountAmount] =
    useState(discountAmount);

  const [localTotalAmount, setLocalTotalAmount] = useState(totalAmount);

  const { value: animatedSubTotal, reset: countSubTotalAmount } = useCountUp({
    isCounting: true,
    start: localSubTotalAmount,
    end: subTotalAmount,
    duration: 0.4,
    onComplete: () => {
      setLocalSubTotalAmount(subTotalAmount);
    },
  });

  const { value: animatedTotalAmount, reset: countTotalAmount } = useCountUp({
    isCounting: true,
    start: localTotalAmount,
    end: totalAmount,
    duration: 0.4,
    onComplete: () => {
      setLocalTotalAmount(totalAmount);
    },
  });

  const { value: animatedDiscountAmount, reset: countDiscountAmount } =
    useCountUp({
      isCounting: true,
      start: localDiscountAmount,
      end: discountAmount,
      duration: 0.4,
      onComplete: () => {
        setLocalDiscountAmount(discountAmount);
      },
    });

  useEffect(() => {
    countSubTotalAmount();
  }, [subTotalAmount]);

  useEffect(() => {
    countDiscountAmount();
  }, [discountAmount]);

  useEffect(() => {
    countTotalAmount();
  }, [totalAmount]);

  return (
    <Box sx={sx.summary}>
      <Box
        sx={{ ...sx.row, mt: 3, mb: 1.5, display: { xs: "none", md: "flex" } }}
      >
        <Typography variant="h5">{total}</Typography>
      </Box>
      <Box sx={sx.row}>
        <Typography variant="bodyMedium">{priceSummaryLabel}</Typography>
        <Typography variant={isTablet ? "titleMedium" : "bodyMedium"}>
          {formatPrice(animatedSubTotal, locale, getCurrency(itinerary))}
        </Typography>
      </Box>
      <Collapse in={discountAmount > 0}>
        <Box sx={sx.row}>
          <Typography variant={"bodyMedium"} color="secondary.main">
            {discount}
          </Typography>
          <Typography
            variant={isTablet ? "titleMedium" : "bodyMedium"}
            color="secondary.main"
          >
            {getStringifiedDiscount(
              formatPrice(
                animatedDiscountAmount,
                locale,
                getCurrency(itinerary),
              ),
            )}
          </Typography>
        </Box>
      </Collapse>
      <Box sx={sx.row}>
        {isDesktop ? (
          <Typography variant={"titleMedium"} weight="bold">
            {totalPriceToPay}
          </Typography>
        ) : (
          <Typography variant={"h5"}>{total}</Typography>
        )}
        <Typography variant="h5">
          {formatPrice(animatedTotalAmount, locale, getCurrency(itinerary))}
        </Typography>
      </Box>
    </Box>
  );
};

const sx: { [key: string]: SxProps } = {
  summary: {
    p: { xs: 2, md: 0 },
    mb: { md: 1 },
  },
  row: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    my: 0.75,
    mx: 0,
  },
};
